import { AsyncPipe, NgClass } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatOption } from "@angular/material/core";
import { MatDialog } from "@angular/material/dialog";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatSelect } from "@angular/material/select";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { SortingList } from "@interfaces/social.i";
import { IonicModule } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Subscription, forkJoin, from, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { OwnedPhotoPopupComponent } from "src/app/components/owned-photo-popup/owned-photo-popup.component";
import { UnsplashPhotoPopupComponent } from "src/app/components/unsplash-photo-popup/unsplash-photo-popup.component";
import { SocialService } from "src/app/services/social.service";
import type { SwiperOptions } from "swiper/types";
import { SwiperModule } from "swiper/angular";
import { InProgressAnimationComponent } from "../../../in-progress-animation/in-progress-animation.component";

@Component({
  selector: "app-fortune-lifestyle",
  templateUrl: "./fortune-lifestyle.component.html",
  styleUrls: ["./fortune-lifestyle.component.scss"],
  standalone: true,
  imports: [
    IonicModule,
    FormsModule,
    SwiperModule,
    NgClass,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    InProgressAnimationComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class FortuneLifestyleComponent implements OnChanges, OnDestroy {
  @Input() lfTabIndex = "";
  @ViewChild("newGallery") newGallery: ElementRef;
  @ViewChild("mineGallery") mineGallery: ElementRef;
  public allSegmentData$ = new BehaviorSubject(null);
  public globalCategoryAlbumData$ = new BehaviorSubject(null);
  public getSocialFortuneLifeStyle$ = new BehaviorSubject(null);
  public selectedLanguage: string;
  public spanish: boolean;
  public category: string;
  public mine: boolean;
  public new: boolean;
  public gridChange: boolean;
  public unsplashImages: Data;
  public searchImages: Data;
  public searchTerms: string;
  public mineImages: Data;
  public publication: Data;
  public isLiked: boolean;
  public likedImages: Data;
  public selectedId: string;
  public allImages: boolean;
  public mineImageData: Data;
  public likedImagesData: Data;
  public unsplashApiImages: string[] = [];
  public ApiImages: boolean;
  public searchItems: string;
  public categories: string[] = [];
  public newVisibleImages: string[] = [];
  public mineVisibleImages: string[] = [];
  public visibleImageNumber = 8;
  loaded: boolean;
  // This is for sorting variable of old or Latest and Liked or all
  sort: SortingList[] = [
    { value: "latest", viewValue: "" },
    { value: "old", viewValue: "" },
  ];
  sortBy: SortingList[] = [
    { value: "all", viewValue: "" },
    { value: "liked", viewValue: "" },
  ];
  selectedValue = this.sort[0].value;
  selectedLikedValue = this.sortBy[0].value;
  private getLifestyleAlbumSubscription: Subscription;
  private getLifestyleCategorySubscription: Subscription;
  constructor(
    private matDialog: MatDialog,
    public translate: TranslateService,
    private socialService: SocialService,
    private authService: AuthService,
    private changeDetectionRef: ChangeDetectorRef
  ) {
    forkJoin([
      this.translate.get("Lifestyle.latest"),
      this.translate.get("Lifestyle.old"),
      this.translate.get("Lifestyle.all"),
      this.translate.get("Lifestyle.like"),
    ]).subscribe((translations: string[]) => {
      this.sort[0].viewValue = translations[0];
      this.sort[1].viewValue = translations[1];
      this.sortBy[0].viewValue = translations[2];
      this.sortBy[1].viewValue = translations[3];
    });
    this.gridChange = true;
    this.allImages = true;
    this.mine = false;
    this.new = true;
    // This method has been used to get random image from the unsplash API
    this.socialService
      .getRandomImage()
      .pipe(
        catchError((err) => {
          // Handle error
          return throwError(err);
        })
      )
      .subscribe((data: Data) => {
        try {
          this.unsplashImages = data;
        } catch (error) {
          // Handle error
          return throwError(error);
        }
      });

    // This method has been used to get selected language
    this.authService?.selectedlanguage$?.subscribe(
      (l) => {
        this.selectedLanguage = l ? l : "en";
        translate.use(l ? l : "en");
        if (l == "es") {
          this.spanish = true;
        }
      },
      (err) => {
        return err;
      }
    );
  }
  ngOnDestroy(): void {
    this.getLifestyleAlbumSubscription.unsubscribe();
    this.getLifestyleCategorySubscription.unsubscribe();
  }
  // This method has been used to set Swiper slider options
  public catSliderOptions: SwiperOptions = {
    speed: 400,
    slidesPerView: 3.5,
    slidesPerGroup: 1,
    slideToClickedSlide: true,
  };
  // This method has been used to open unsplash photo popup
  public unsplashPhotoPopup(imageURL: string, subcategory: string, category: string, liked: boolean) {
    this.matDialog.open(UnsplashPhotoPopupComponent, {
      panelClass: "unsplash-dialog-size",
      backdropClass: "unsplash-back-drop-color",
      data: { imageURL, subcategory, category, liked },
    });
  }
  // This method has been used to open owned photo popup
  public ownedPhotoPopup(
    imageURL: string,
    subcategory: string,
    searchTerms: string,
    category: string,
    mineImageData: Data
  ) {
    this.matDialog.open(OwnedPhotoPopupComponent, {
      panelClass: "own-dialog-size",
      backdropClass: "own-back-drop-color",
      data: { imageURL, subcategory, searchTerms, category, mineImageData },
    });
  }

  ngOnInit() {
    this.loaded = true;
    // This method has been used to get the All albums data from social service
    this.socialService.socialFortuneAlbums$
      .pipe(
        catchError((err) => {
          // Handle the error
          return throwError(err);
        })
      )
      .subscribe((data: Data) => {
        try {
          this.allSegmentData$.next(data);
        } catch (err) {
          // Handle the error
          return throwError(err);
        }
      });

    // To get the lifestyle categories from global document
    this.getLifestyleAlbumSubscription = from(this.socialService.getLifestyleAlbumCategories())
      .pipe(
        catchError((err) => {
          // Handle the error here and return it as an observable
          console.error('Error fetching lifestyle album categories:', err);
          return throwError(err);
        })
      )
      .subscribe(
        (data: Data) => {
          if (data) {
            const lifeStyleAlbumCategory = data?.lifestyle_album_categories;

            // Sort and filter the categories
            const sortedCategories = Object.keys(lifeStyleAlbumCategory)
              .filter((key) => lifeStyleAlbumCategory[key]?.visible) // Ensure visible is checked safely
              .sort((a, b) => lifeStyleAlbumCategory[a]?.order - lifeStyleAlbumCategory[b]?.order) // Safely access the order
              .reduce((obj, key) => {
                obj[key] = lifeStyleAlbumCategory[key];
                return obj;
              }, {});

            // Push sorted categories into the observable stream
            this.globalCategoryAlbumData$.next(sortedCategories);

            // Subscribe to the global category album data and handle it
            this.getLifestyleCategorySubscription = this.globalCategoryAlbumData$.subscribe((sortedData) => {
              this.categories = Object.keys(sortedData);
              this.category = this.categories[0]; // Default to the first category
              this.segmentChanged(); // Make sure segmentChanged is defined
            });
          }
        },
        (err) => {
          // Handle subscription error if necessary
          console.error('Subscription error:', err);
        }
      );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const segments = changes?.lfTabIndex?.currentValue;
    if (segments) {
      this.category = segments;
    }
  }
  // This method has been used to get the mine event
  mineNew(event: string) {
    this.changeDetectionRef.markForCheck();
    if (event == "mine") {
      this.mine = true;
      this.new = false;
    } else if (event == "new") {
      this.mine = false;
      this.new = true;
    }
  }

  // This method has been used to search image according to category  values from the Unsplash search API
  public searchValue(searchTerm: string) {
    this.searchTerms = searchTerm;
    this.socialService
      .searchImage(searchTerm)
      .pipe(
        catchError((err) => {
          // Handle error
          return throwError(err);
        })
      )
      .subscribe((data: Data) => {
        try {
          this.searchImages = data;
        } catch (err) {
          // Handle error
          return throwError(err);
        }
      });
  }
  // This method has been used to select ion chip name according to category
  public selectIonType(searchTerms: string, selectedId: string) {
    this.searchTerms = searchTerms;
    this.selectedId = selectedId;
    // This method has been used to search image according to category  values from the Unsplash search API
    this.socialService
      .searchImage(searchTerms)
      .pipe(
        catchError((err) => {
          // Handle error
          return throwError(err);
        })
      )
      .subscribe((data: Data) => {
        try {
          this.searchImages = data;
        } catch (err) {
          // Handle error
          return throwError(err);
        }
      });
    // This method has been used to get search image from the unsplash CF
    this.getUnsplashImages(this.selectedId);
    // This method has been used to get mine section images
    this.socialService
      .getMineSectionImages(this.category, this.selectedId)
      .pipe(
        catchError((err) => {
          // Handle error
          return throwError(err);
        })
      )
      .subscribe((res: Data) => {
        try {
          const data = res as Data;
          this.mineImageData = data;
          this.mineImages = data?.images?.reverse();
          this.mineVisibleImages = [];
          this.loadMoreMineImages();
        } catch (err) {
          // Handled error
          return throwError(err);
        }
      });
    // This method has been used to get liked images
    this.socialService
      .getLikedImages(this.selectedId)
      .pipe(
        catchError((err) => {
          // Handle error
          return throwError(err);
        })
      )
      .subscribe((data: Data) => {
        try {
          this.likedImages = data;
          this.likedImagesData = data;
        } catch (err) {
          // Handle error
          return throwError(err);
        }
      });
  }

  // This method has been used to get the images when segment changed
  public segmentChanged() {
    this.socialService
      .getLifestyleAlbum(this.category)
      .pipe(
        catchError((err) => {
          // Handle error
          return throwError(err);
        })
      )
      .subscribe((categoryAlbums: Data) => {
        this.mine = false;
        this.new = true;
        categoryAlbums = this.modifyCategoryAlbums(categoryAlbums, this.category);
        try {
          // This method has been used to sort the category albums based on order and visibility
          const sortedCategoryAlbums = Object.keys(categoryAlbums)
            .filter((key) => categoryAlbums[key].visible)
            .sort((a, b) => categoryAlbums[a].order - categoryAlbums[b].order)
            .reduce((arr, key) => {
              arr.push(categoryAlbums[key]);
              return arr;
            }, []);
          this.searchTerms = sortedCategoryAlbums?.[0]?.content?.[this.selectedLanguage]?.title;
          this.selectedId = sortedCategoryAlbums?.[0]?.content?.en?.title;
          this.getSocialFortuneLifeStyle$.next(sortedCategoryAlbums);
          // This method has been used to get Liked image according to selected id from the unsplash CF
          this.socialService
            .getLikedImages(this.selectedId)
            .pipe(
              catchError((err) => {
                // Handle error
                return throwError(err);
              })
            )
            .subscribe((data: Data) => {
              try {
                this.likedImages = data;
              } catch (err) {
                // Handle error
                return throwError(err);
              }
            });
          // This method has been used to get mine section images
          this.socialService
            .getMineSectionImages(this.category, this.selectedId)
            .pipe(
              catchError((err) => {
                // Handle error
                return throwError(err);
              })
            )
            .subscribe((res: Data) => {
              try {
                let data = res as Data;
                this.mineImageData = data;
                this.mineImages = data?.images?.reverse();
                this.mineVisibleImages = [];
                this.loadMoreMineImages();
              } catch (err) {
                // Handle error
                return throwError(err);
              }
            });
          // This method has been used to get search image from the unsplash Search API
          this.socialService
            .searchImage(this.searchTerms)
            .pipe(
              catchError((err) => {
                // Handle error
                return throwError(err);
              })
            )
            .subscribe((data: Data) => {
              try {
                this.searchImages = data;
              } catch (err) {
                // Handle error
                return throwError(err);
              }
            });
          this.getUnsplashImages(this.selectedId);
        } catch (err) {
          return throwError(err);
        }
      });
    this.selectedValue = this.sort[0]?.value;
  }
  // This method has been used to filter the mine section images according to latest and old images
  public minFilteredOption(option: string) {
    // This method has been used to get search image from the unsplash CF
    this.getUnsplashImages(this.selectedId);
    // This method has been used to get mine section images
    this.socialService
      .getMineSectionImages(this.category, this.selectedId)
      .pipe(
        catchError((err) => {
          // Handle error
          return throwError(err);
        })
      )
      .subscribe((res: Data) => {
        try {
          const data = res as Data;
          this.mineImages = data?.images?.reverse();
          this.mineImageData = data;
          this.changeDetectionRef.markForCheck();
          // This switch case has been used to filter the images
          switch (option) {
            case "latest":
              this.mineImages;
              break;
            case "old":
              this.mineImages = this.mineImages?.reverse();
              break;
          }
          this.mineVisibleImages = [];
          this.loadMoreMineImages();
        } catch (err) {
          // Handle error
          return throwError(err);
        }
      });
  }
  // This method has been used to filter the images according to option all ,liked and unlike
  public filteredOption(option: string, searchId: string) {
    // This switch case has been used to get search image from the unsplash CF
    switch (option) {
      case "all":
        this.allImages = true;
        this.isLiked = false;
        break;
      case "liked":
        this.isLiked = true;
        this.allImages = false;
        this.socialService
          .getLikedImages(searchId)
          .pipe(
            catchError((err) => {
              // Handle error
              return throwError(err);
            })
          )
          .subscribe((data: Data) => {
            try {
              this.likedImages = data;
            } catch (err) {
              // Handle error
              return throwError(err);
            }
          });
        break;
      case "unliked":
        this.isLiked = false;
        break;
    }
  }

  // This method has been used to get image from the unsplash CF
  getUnsplashImages(searchTerms: string) {
    if (this.socialService.searchTermResponses.hasOwnProperty(searchTerms)) {
      // Retrieve the data from searchTermResponses based on the searchTerms parameter
      const responseData = this.socialService.searchTermResponses[searchTerms];
      let imageUrls = responseData;
      let array = [];
      let url = "";
      imageUrls.map((Url) => {
        url = Url.url;
        const MatchedURLs = url.match("800x800.webp");
        if (MatchedURLs) {
          this.allImages = false;
          this.ApiImages = true;
          array.push(MatchedURLs?.input.toString());
          this.newVisibleImages = [];
          this.unsplashApiImages = array;
        }
      });
      this.loadMoreNewImages();
      if (responseData == null) {
        this.allImages = true;
        this.ApiImages = false;
      }
    } else {
      this.allImages = true;
      this.ApiImages = false;
    }
  }

  // This function is used to make home album as default for real estate
  modifyCategoryAlbums(categoryAlbums: Data, category: string) {
    const realEstate = "real_estate";
    if (category === realEstate) {
      const homeAlbumIndex = categoryAlbums.findIndex((album: { selectedId: string }) => album.selectedId === "home");
      if (homeAlbumIndex !== -1) {
        categoryAlbums[homeAlbumIndex].order = 1;
      }
    }
    return categoryAlbums;
  }

  loadMoreNewImages() {
    if (this.unsplashApiImages) {
      const startIndex = this.newVisibleImages.length;
      const endIndex = Math.min(startIndex + this.visibleImageNumber, this.unsplashApiImages.length);
      for (let i = startIndex; i < endIndex; i++) {
        this.newVisibleImages.push(this.unsplashApiImages[i]);
      }
    }
  }

  loadMoreMineImages() {
    if (this.mineImages) {
      const startIndex = this.mineVisibleImages.length;
      const endIndex = Math.min(startIndex + this.visibleImageNumber, this.mineImages.length);
      for (let i = startIndex; i < endIndex; i++) {
        this.mineVisibleImages.push(this.mineImages[i]);
      }
    }
  }

  @HostListener("scroll", ["$event"])
  onScroll() {
    if (this.newGallery) {
      const newGalleryElement = this.newGallery.nativeElement;
      if (newGalleryElement.scrollHeight - newGalleryElement.scrollTop === newGalleryElement.clientHeight) {
        this.loadMoreNewImages();
      }
    }
    if (this.mineGallery) {
      const mineGalleryElement = this.mineGallery.nativeElement;
      if (mineGalleryElement.scrollHeight - mineGalleryElement.scrollTop === mineGalleryElement.clientHeight) {
        this.loadMoreMineImages();
      }
    }
  }
}
